import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import {
  Badge,
  Button,
  Form,
  Modal,
  Stack,
} from 'react-bootstrap';
import AdminSidebar from '../../components/AdminSidebar';
import {
  AdminNotificationsProvider,
  useAdminNotifications,
} from '../../components/AdminNotifications';
import useAdminGuard from '../../hooks/useAdminGuard';
import { hasPermission, PERMISSION_IDS } from '../../utils/permissions';
import { useAuth } from '../../context/AuthContext';
import { buildApiUrl, getApiBaseUrl } from '../../config/apiConfig';
import '../../styles/admin.css';
import './AdminCourses.css';
import { logError } from '../../utils/logger';

const resolveImageUrl = (source) => {
  if (!source) return null;
  const sanitized = String(source).replace(/\\/g, '/').replace(/\s/g, '%20');
  if (/^https?:\/\//i.test(sanitized)) return sanitized;
  const base = getApiBaseUrl();
  if (sanitized.startsWith('/')) return `${base}${sanitized}`;
  if (sanitized.startsWith('uploads/')) return `${base}/${sanitized}`;
  return `${base}/uploads/${sanitized}`;
};

const CourseSkeleton = () => (
  <div className="AC-skeleton">
    <div className="AC-skeleton-image" />
    <div className="AC-skeleton-body">
      <div className="AC-skeleton-line AC-skeleton-line--short" />
      <div className="AC-skeleton-line AC-skeleton-line--medium" />
      <div className="AC-skeleton-line AC-skeleton-line--long" />
      <div className="AC-skeleton-line AC-skeleton-line--medium" />
    </div>
  </div>
);

const StatsCard = ({ icon, label, value, variant }) => (
  <div className="AC-stat-card">
    <div className={classNames('AC-stat-icon', `AC-stat-icon--${variant}`)}>
      <i className={`bi ${icon}`} aria-hidden="true" />
    </div>
    <div className="AC-stat-content">
      <div className="AC-stat-value">{value}</div>
      <div className="AC-stat-label">{label}</div>
    </div>
  </div>
);

const EmptyState = ({ onCreate, showArchived }) => (
  <div className="AC-empty-state">
    <i className="bi-journal-text AC-empty-icon" />
    <h5 className="AC-empty-title">
      {showArchived ? 'Aún no hay cursos archivados' : 'Aún no hay cursos'}
    </h5>
    <p className="AC-empty-description">
      {showArchived
        ? 'No hay cursos archivados en este momento.'
        : 'Crea tu primer curso o importa contenidos existentes para comenzar a vender de inmediato.'}
    </p>
    {!showArchived && (
      <Button variant="primary" onClick={onCreate}>
        <i className="bi-plus-circle me-2" />
        Crear curso
      </Button>
    )}
  </div>
);

const AdminCoursesInner = () => {
  const { token, user } = useAdminGuard();
  const { notify } = useAdminNotifications();
  const navigate = useNavigate();

  // Verificar permisos específicos de cursos
  const hasViewPanel = useMemo(() => hasPermission(user, PERMISSION_IDS.VER_PANEL_CURSOS), [user]);
  const hasViewCourses = useMemo(() => hasPermission(user, PERMISSION_IDS.VER_CURSOS), [user]);
  const hasEditContent = useMemo(() => hasPermission(user, PERMISSION_IDS.EDITAR_CONTENIDO_CURSOS), [user]);
  const hasActivateCourses = useMemo(() => hasPermission(user, PERMISSION_IDS.ACTIVAR_CURSOS), [user]);
  const hasArchiveCourse = useMemo(() => hasPermission(user, PERMISSION_IDS.ARCHIVAR_CURSO), [user]);

  // Si no tiene permiso para ver el panel, redirigir
  useEffect(() => {
    if (user && !hasViewPanel && !hasViewCourses) {
      notify({
        title: 'Acceso denegado',
        message: 'No tienes permisos para acceder a la gestión de cursos.',
        variant: 'error',
      });
      navigate('/403', { replace: true });
    }
  }, [user, hasViewPanel, hasViewCourses, navigate, notify]);

  const [stats, setStats] = useState({ activeCourses: 0, inactiveCourses: 0, archivedCourses: 0 });
  const [courses, setCourses] = useState([]);
  const [search, setSearch] = useState('');
  const [loadingStats, setLoadingStats] = useState(true);
  const [loadingCourses, setLoadingCourses] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createCourseName, setCreateCourseName] = useState('');
  const [creatingCourse, setCreatingCourse] = useState(false);
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [showCreateCategoryModal, setShowCreateCategoryModal] = useState(false);
  const [createCategoryName, setCreateCategoryName] = useState('');
  const [creatingCategory, setCreatingCategory] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState(false);

  const fetchStats = async (isRefresh = false) => {
    if (!token) return;
    if (!isRefresh) setLoadingStats(true);
    try {
      const response = await fetch(buildApiUrl('/api/courses/stats'), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (response.status === 429) {
        // Rate limit - esperar un poco y reintentar
        await new Promise(resolve => setTimeout(resolve, 2000));
        const retryResponse = await fetch(buildApiUrl('/api/courses/stats'), {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (!retryResponse.ok) {
          throw new Error(`Error al cargar estadísticas (${retryResponse.status})`);
        }
        const data = await retryResponse.json();
        setStats({
          activeCourses: data.activeCourses ?? 0,
          inactiveCourses: data.inactiveCourses ?? 0,
          archivedCourses: data.archivedCourses ?? 0,
        });
        return;
      }
      
      if (!response.ok) {
        throw new Error(`Error al cargar estadísticas (${response.status})`);
      }
      const data = await response.json();
      setStats({
        activeCourses: data.activeCourses ?? 0,
        inactiveCourses: data.inactiveCourses ?? 0,
        archivedCourses: data.archivedCourses ?? 0,
      });
    } catch (err) {
      logError('[AdminCourses] Error fetching stats:', err);
      if (err.message && !err.message.includes('429')) {
        notify({
          title: 'No pudimos obtener las estadísticas',
          message: err.message,
          variant: 'error',
        });
      }
    } finally {
      setLoadingStats(false);
    }
  };

  const fetchCategories = async () => {
    if (!token) return;
    setLoadingCategories(true);
    try {
      const response = await fetch(buildApiUrl('/api/courses/categories'), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error(`Error al cargar categorías (${response.status})`);
      }
      const data = await response.json();
      setCategories(Array.isArray(data.categories) ? data.categories : []);
    } catch (err) {
      logError('[AdminCourses] Error fetching categories:', err);
    } finally {
      setLoadingCategories(false);
    }
  };

  const fetchCourses = async (isRefresh = false) => {
    if (!token) return;
    if (!isRefresh) setLoadingCourses(true);
    try {
      const url = buildApiUrl(`/api/courses?archived=${showArchived}`);
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (response.status === 429) {
        // Rate limit - esperar un poco y reintentar
        await new Promise(resolve => setTimeout(resolve, 2000));
        const retryResponse = await fetch(url, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (!retryResponse.ok) {
          throw new Error(`Error al cargar los cursos (${retryResponse.status})`);
        }
        const data = await retryResponse.json();
        const coursesList = Array.isArray(data.courses) ? data.courses : [];
        const normalizedCourses = coursesList.map((course) => ({
          ...course,
          estado: course.estado || 'inactivo',
          archivado: course.archivado || false,
          titulo: course.titulo || course.title || 'Curso sin título',
        }));
        setCourses(normalizedCourses);
        return;
      }
      
      if (!response.ok) {
        throw new Error(`Error al cargar los cursos (${response.status})`);
      }
      const data = await response.json();
      const coursesList = Array.isArray(data.courses) ? data.courses : [];
      // Asegurar que todos los cursos tengan un estado válido
      const normalizedCourses = coursesList.map((course) => ({
        ...course,
        estado: course.estado || 'inactivo',
        archivado: course.archivado || false,
        titulo: course.titulo || course.title || 'Curso sin título',
      }));
      setCourses(normalizedCourses);
    } catch (err) {
      logError('[AdminCourses] Error fetching courses:', err);
      if (err.message && !err.message.includes('429')) {
        notify({
          title: 'No pudimos cargar los cursos',
          message: err.message,
          variant: 'error',
        });
      }
    } finally {
      setLoadingCourses(false);
    }
  };

  useEffect(() => {
    if (!token) return;
    let isMounted = true;
    
    const loadData = async () => {
      if (isMounted) {
        await Promise.all([fetchStats(), fetchCourses(), fetchCategories()]);
      }
    };
    
    loadData();
    
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, showArchived]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await Promise.all([fetchStats(true), fetchCourses(true)]);
    setIsRefreshing(false);
    notify({
      title: 'Datos actualizados',
      message: 'La lista de cursos y estadísticas se sincronizaron correctamente.',
      variant: 'success',
    });
  };

  const filteredCourses = useMemo(() => {
    if (!search.trim()) return courses;
    const term = search.trim().toLowerCase();
    return courses.filter((course) => {
      const title = course.titulo || course.title || '';
      const description = course.descripcionBreve || course.descripcion || '';
      return (
        title.toLowerCase().includes(term) ||
        description.toLowerCase().includes(term) ||
        String(course.id).includes(term)
      );
    });
  }, [courses, search]);

  const handleCreateCourse = () => {
    setShowCreateModal(true);
  };

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    setCreateCourseName('');
    setSelectedCategoryId('');
  };

  const handleSubmitCreateCourse = async (event) => {
    event.preventDefault();
    if (!createCourseName.trim()) {
      notify({
        title: 'Nombre requerido',
        message: 'Por favor ingresa un nombre para el curso.',
        variant: 'warning',
      });
      return;
    }

    setCreatingCourse(true);
    try {
      const response = await fetch(buildApiUrl('/api/courses'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ 
          titulo: createCourseName.trim(),
          categoriaId: selectedCategoryId || null
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Error desconocido' }));
        throw new Error(errorData.error || `Error al crear curso (${response.status})`);
      }

      const data = await response.json();
      notify({
        title: 'Curso creado',
        message: 'El curso se creó exitosamente. Redirigiendo al detalle...',
        variant: 'success',
      });

      handleCloseCreateModal();
      await Promise.all([fetchStats(true), fetchCourses(true)]);
      
      if (data.courseId) {
        setTimeout(() => {
          navigate(`/admin/courses/${data.courseId}`);
        }, 1000);
      }
    } catch (err) {
      notify({
        title: 'Error al crear curso',
        message: err.message,
        variant: 'error',
      });
    } finally {
      setCreatingCourse(false);
    }
  };

  const handleViewArchived = () => {
    setShowArchived(!showArchived);
  };

  const handleCreateCategory = () => {
    setShowCreateCategoryModal(true);
  };

  const handleCloseCreateCategoryModal = () => {
    setShowCreateCategoryModal(false);
    setCreateCategoryName('');
  };

  const handleSubmitCreateCategory = async (event) => {
    event.preventDefault();
    if (!createCategoryName.trim()) {
      notify({
        title: 'Nombre requerido',
        message: 'Por favor ingresa un nombre para la categoría.',
        variant: 'warning',
      });
      return;
    }

    setCreatingCategory(true);
    try {
      const response = await fetch(buildApiUrl('/api/courses/categories'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ 
          nombre: createCategoryName.trim()
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Error desconocido' }));
        throw new Error(errorData.error || `Error al crear categoría (${response.status})`);
      }

      notify({
        title: 'Categoría creada',
        message: 'La categoría se creó exitosamente.',
        variant: 'success',
      });

      handleCloseCreateCategoryModal();
      await fetchCategories();
    } catch (err) {
      notify({
        title: 'Error al crear categoría',
        message: err.message,
        variant: 'error',
      });
    } finally {
      setCreatingCategory(false);
    }
  };

  return (
    <div className="admin-dashboard">
      <div className="admin-layout">
        <AdminSidebar
          activeKey="courses"
          onNavigate={(item) => {
            if (item.path) {
              navigate(item.path);
              return;
            }
            notify({
              title: 'Sección en desarrollo',
              message: 'Estamos migrando esta sección al nuevo panel.',
              variant: 'info',
            });
          }}
        />
        <main className="admin-main">
          <div className="AC-container">
          <div className="AC-header">
            <div className="AC-header-content">
              <h1 className="AC-title">
                <i className="bi-mortarboard-fill text-primary" />
                Gestión de Cursos
              </h1>
              <p className="AC-subtitle">
                Administra tus cursos, controla sus estados y accede rápidamente a sus contenidos.
              </p>
            </div>
            <div className="AC-actions">
              {hasArchiveCourse && (
              <Button
                variant={showArchived ? 'secondary' : 'outline-secondary'}
                onClick={handleViewArchived}
              >
                <i className="bi-archive me-2" />
                {showArchived ? 'Ver activos' : 'Ver archivados'}
              </Button>
              )}
              {hasEditContent && (
              <Button variant="primary" onClick={handleCreateCourse}>
                <i className="bi-plus-circle me-2" />
                Crear curso
              </Button>
              )}
            </div>
          </div>

          <section className="AC-stats-section">
            <div className="AC-stats-grid">
              <StatsCard
                icon="bi-check-circle-fill"
                label="Cursos activos"
                value={loadingStats ? '--' : stats.activeCourses}
                variant="success"
              />
              <StatsCard
                icon="bi-pause-circle-fill"
                label="Cursos inactivos"
                value={loadingStats ? '--' : stats.inactiveCourses}
                variant="warning"
              />
              <StatsCard
                icon="bi-archive-fill"
                label="Cursos archivados"
                value={loadingStats ? '--' : stats.archivedCourses}
                variant="muted"
              />
            </div>
          </section>

          {/* Sección de Categorías */}
          <section className="AC-categories-section">
            <div className="AC-categories-header" onClick={() => setExpandedCategories(!expandedCategories)}>
              <div>
                <h2 className="AC-categories-title">
                  <i className={`bi-chevron-${expandedCategories ? 'down' : 'right'} AC-categories-chevron me-2`} />
                  <i className="bi-folder-fill me-2" />
                  Categorías de Cursos
                </h2>
                {expandedCategories && (
                  <p className="AC-categories-subtitle">
                    Gestiona las categorías para organizar tus cursos.
                  </p>
                )}
              </div>
              {expandedCategories && hasEditContent && (
                <Button 
                  variant="primary" 
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCreateCategory();
                  }}
                >
                  <i className="bi-plus-circle me-2" />
                  Crear Categoría
                </Button>
              )}
            </div>
            {expandedCategories && (
              <div className="AC-categories-content">
                {loadingCategories ? (
                  <div className="AC-loading">
                    <div className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                    <span>Cargando categorías...</span>
                  </div>
                ) : categories.length === 0 ? (
                  <div className="AC-empty-state">
                    <i className="bi-folder-x AC-empty-icon" />
                    <h5 className="AC-empty-title">Aún no hay categorías</h5>
                    <p className="AC-empty-description">
                      Crea tu primera categoría para organizar tus cursos.
                    </p>
                    {hasEditContent && (
                      <Button variant="primary" onClick={handleCreateCategory}>
                        <i className="bi-plus-circle me-2" />
                        Crear categoría
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="AC-categories-grid">
                    {categories.map((category) => (
                      <div key={category.id} className="AC-category-card">
                        <div className="AC-category-icon">
                          <i className="bi-folder-fill" />
                        </div>
                        <div className="AC-category-content">
                          <h3 className="AC-category-name">{category.nombre}</h3>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </section>

          <section className="AC-search-section">
            <div className="AC-search-card">
              <div className="AC-search-content">
                <Form.Control
                  type="search"
                  placeholder="Buscar por nombre, descripción o ID..."
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  className="AC-search-input"
                />
                <div className="AC-search-actions">
                  <Button
                    variant="outline-primary"
                    onClick={() => setSearch('')}
                    disabled={!search}
                    size="sm"
                  >
                    Limpiar
                  </Button>
                  <Button variant="primary" onClick={handleRefresh} disabled={isRefreshing} size="sm">
                    {isRefreshing ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        />
                        Actualizando
                      </>
                    ) : (
                      <>
                        <i className="bi-arrow-repeat me-2" />
                        Actualizar
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </section>

          <section className="AC-courses-section">
            {loadingCourses ? (
              <div className="AC-courses-grid">
                {Array.from({ length: 6 }).map((_, index) => (
                  <CourseSkeleton key={index} />
                ))}
              </div>
            ) : filteredCourses.length === 0 ? (
              <EmptyState onCreate={handleCreateCourse} showArchived={showArchived} />
            ) : (
              <div className="AC-courses-grid">
                  {filteredCourses.map((course) => {
                    const imageUrl = resolveImageUrl(course.imagen);
                    const status =
                      course.estado === 'activo'
                        ? { label: 'Activo', variant: 'success' }
                        : course.estado === 'pendiente'
                        ? { label: 'Pendiente', variant: 'warning' }
                        : { label: 'Inactivo', variant: 'secondary' };

                    return (
                      <div key={course.id} className="AC-course-card">
                        {imageUrl ? (
                          <img src={imageUrl} alt={course.titulo || 'Curso'} className="AC-course-image" />
                        ) : (
                          <div className="AC-course-image-placeholder">
                            <i className="bi-journal-album" />
                          </div>
                        )}
                        <div className="AC-course-body">
                          <div className="AC-course-badges">
                            <Badge bg={status.variant}>{status.label}</Badge>
                            {course.archivado && <Badge bg="dark">Archivado</Badge>}
                            {course.discountPercent > 0 && (
                              <Badge bg="info">- {course.discountPercent}%</Badge>
                            )}
                          </div>
                          <h3 className="AC-course-title">{course.titulo || 'Curso sin título'}</h3>
                          <p className="AC-course-description">
                            {course.descripcionBreve || 'Sin descripción disponible.'}
                          </p>
                          <div className="AC-course-stats">
                            <span className="AC-course-stat-item">
                              <i className="bi-diagram-3" />
                              {course.moduleCount ?? 0} módulos
                            </span>
                            <span className="AC-course-stat-item">
                              <i className="bi-play-circle" />
                              {course.classCount ?? 0} clases
                            </span>
                            <span className="AC-course-stat-item">
                              <i className="bi-clock-history" />
                              {course.totalMinutes ?? 0} min
                            </span>
                          </div>
                          <Button
                            variant="primary"
                            className="AC-course-action"
                            onClick={() => navigate(`/admin/courses/${course.id}`)}
                          >
                            <i className="bi-box-arrow-in-right me-2" />
                            Ver detalle
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
            )}
          </section>
          </div>
        </main>
      </div>

      <Modal show={showCreateModal} onHide={handleCloseCreateModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi-plus-circle me-2" />
            Crear Nuevo Curso
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmitCreateCourse}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Nombre del Curso *</Form.Label>
              <Form.Control
                type="text"
                placeholder="Ingresa el nombre del curso"
                value={createCourseName}
                onChange={(event) => setCreateCourseName(event.target.value)}
                required
                autoFocus
                maxLength={100}
                disabled={creatingCourse}
              />
              <Form.Text className="text-muted">
                El curso se creará en estado inactivo. Podrás configurarlo después.
              </Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <Form.Label>Categoría (opcional)</Form.Label>
                {hasEditContent && (
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 text-primary"
                    onClick={(e) => {
                      e.preventDefault();
                      handleCreateCategory();
                    }}
                    disabled={creatingCourse}
                  >
                    <i className="bi-plus-circle me-1" />
                    Crear categoría
                  </Button>
                )}
              </div>
              <Form.Select
                value={selectedCategoryId}
                onChange={(e) => setSelectedCategoryId(e.target.value)}
                disabled={creatingCourse || loadingCategories}
              >
                <option value="">Sin categoría</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.nombre}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseCreateModal} disabled={creatingCourse}>
              Cancelar
            </Button>
            <Button variant="primary" type="submit" disabled={creatingCourse || !createCourseName.trim()}>
              {creatingCourse ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  />
                  Creando...
                </>
              ) : (
                <>
                  <i className="bi-check-circle me-2" />
                  Crear Curso
                </>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modal para crear categoría */}
      <Modal show={showCreateCategoryModal} onHide={handleCloseCreateCategoryModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi-folder-plus me-2" />
            Crear Nueva Categoría
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmitCreateCategory}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Nombre de la Categoría *</Form.Label>
              <Form.Control
                type="text"
                placeholder="Ingresa el nombre de la categoría"
                value={createCategoryName}
                onChange={(event) => setCreateCategoryName(event.target.value)}
                required
                autoFocus
                maxLength={100}
                disabled={creatingCategory}
              />
              <Form.Text className="text-muted">
                El nombre debe ser único y descriptivo.
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseCreateCategoryModal} disabled={creatingCategory}>
              Cancelar
            </Button>
            <Button variant="primary" type="submit" disabled={creatingCategory || !createCategoryName.trim()}>
              {creatingCategory ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                  Creando...
                </>
              ) : (
                <>
                  <i className="bi-check-circle me-2" />
                  Crear categoría
                </>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

const AdminCoursesPage = () => (
  <AdminNotificationsProvider>
    <AdminCoursesInner />
  </AdminNotificationsProvider>
);

export default AdminCoursesPage;

